import React, { FC } from 'react';

import { UseQueryResult } from '@tanstack/react-query';
import classNames from 'classnames';

import { EvSection, EvTitle, TABLE_SR_SUMMARY_IDS } from '@evinced-private/ui-common';

import {
	WFA_DASHBOARD_PAGE_TABLE_SECTION_ID,
	WFA_DASHBOARD_PAGE_TABLE_TITLE_ID
} from 'data-automation-ids/WfaDashboardAutomationIds';
import { IWfaSessionRow, TWfaSessionsResult } from 'src/apps/wfa/services/WfaDashboardServiceTypes';
import { PlatformTable } from 'src/common/components/platform-table/PlatformTable';
import { TableSkeleton } from 'src/common/components/skeletons/table-skeleton/TableSkeleton';
import { TableSelectionActions } from 'src/common/components/table-selection-actions/TableSelectionActions';
import { WFA_SESSIONS_TABLE_ID } from 'src/common/pages/dashboards-container/dashboard_consts';

import { getWfaSessionsColumns } from './WfaSessionsTableHelper';

import './WfaSessionsTable.scss';

const SELECT_SESSIONS_PROMPT = 'Select one or more sessions to update the labels';
const ADD_LABELS_BUTTON_LABEL = 'Add label';
const CHANGE_LABEL_VALUE_BUTTON_LABEL = 'Change label value';
const REMOVE_LABELS_BUTTON_LABEL = 'Remove label';

interface IWfaDashboardTableProps {
	sessionsQuery: UseQueryResult<TWfaSessionsResult>;
	unfilteredTotal: number;
	isLabelsToggleEnabled?: boolean;
	isNewPlatformEnabled?: boolean;
	selectedRows: IWfaSessionRow[];
	tableKey: number;
	onSelectRow: (row: IWfaSessionRow, isSelect: boolean) => void;
	onSelectAll: (isSelect: boolean, rows: IWfaSessionRow[]) => void;
	onClearSelection: () => void;
}

const TABLE_KEY_FIELD = 'sessionId';
const TABLE_TITLE = 'WFA Sessions';
const TABLE_CAPTION = 'WFA Sessions table';
const DATA_TYPE = 'sessions';

export const WfaSessionsTable: FC<IWfaDashboardTableProps> = ({
	sessionsQuery,
	unfilteredTotal,
	isLabelsToggleEnabled,
	isNewPlatformEnabled,
	selectedRows,
	tableKey,
	onSelectRow,
	onSelectAll,
	onClearSelection
}) => {
	const total: number = sessionsQuery.data?.total ?? 0;

	const isTableLoading =
		sessionsQuery.isLoading ||
		sessionsQuery.isPreviousData ||
		(sessionsQuery.isStale && sessionsQuery.isRefetching);

	return (
		<EvSection
			ariaLabel="Web Flow Analyzer sessions Table"
			ariaLive="polite"
			ariaDescribedby={TABLE_SR_SUMMARY_IDS}
			skipLinkId="main-section"
			className={classNames('dashboard-section', 'wfa-sessions-table')}
			dataAutomationId={WFA_DASHBOARD_PAGE_TABLE_SECTION_ID}
		>
			<EvTitle
				titleText="Uploaded sessions"
				headingLevel={2}
				className="page-heading"
				dataAutomationId={WFA_DASHBOARD_PAGE_TABLE_TITLE_ID}
			/>
			{isNewPlatformEnabled && (
				<TableSelectionActions
					selectedCount={selectedRows.length}
					onClearSelection={onClearSelection}
					processingMessage={null}
					promptText={SELECT_SESSIONS_PROMPT}
					itemName="session"
					addLabelButtonLabel={ADD_LABELS_BUTTON_LABEL}
					changeLabelValueButtonLabel={CHANGE_LABEL_VALUE_BUTTON_LABEL}
					removeLabelsButtonLabel={REMOVE_LABELS_BUTTON_LABEL}
					// eslint-disable-next-line @typescript-eslint/no-empty-function
					onAddLabel={() => {}} // TODO: Implement add label functionality
					// eslint-disable-next-line @typescript-eslint/no-empty-function
					onChangeLabelValue={() => {}} // TODO: Implement change label value functionality
					// eslint-disable-next-line @typescript-eslint/no-empty-function
					onRemoveLabels={() => {}} // TODO: Implement remove labels functionality
				/>
			)}
			{isTableLoading ? (
				<TableSkeleton columns={5} rowCount={7} />
			) : (
				<PlatformTable
					key={tableKey}
					isTableLoading={isTableLoading}
					tableId={WFA_SESSIONS_TABLE_ID}
					tableData={sessionsQuery.data?.rows}
					totalPaginationableResults={total}
					totalCount={unfilteredTotal}
					persistTableState
					columns={getWfaSessionsColumns(isLabelsToggleEnabled)}
					options={{
						keyField: TABLE_KEY_FIELD,
						title: TABLE_TITLE,
						caption: TABLE_CAPTION,
						pagination: true,
						dataType: DATA_TYPE,
						remote: true,
						selectRow: isNewPlatformEnabled
							? {
									mode: 'checkbox',
									onSelect: onSelectRow,
									onSelectAll
								}
							: undefined
					}}
				/>
			)}
		</EvSection>
	);
};
